import React, { useState, useMemo, useEffect, useCallback } from "react";
import axios from "axios";
import debounce from "lodash.debounce";
import { TextField, Autocomplete, CircularProgress, Button, Box, Chip } from "@mui/material";

const UserSearchInput = ({ token, groupId, onUserAdded }) => {
    const [options, setOptions] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(false);
    const [groupUsers, setGroupUsers] = useState([]);

    useEffect(() => {
        const fetchGroupUsers = async () => {
            if (!groupId || !token) return;

            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/groups/${groupId}?populate=users`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                const groupData = res.data?.data;
                const users = groupData?.attributes?.users?.data || [];

                setGroupUsers(users.map(user => ({
                    id: user.id,
                    ...user.attributes
                })));
            } catch (err) {
                console.error("Błąd pobierania użytkowników grupy", err);
                setGroupUsers([]);
            }
        };

        fetchGroupUsers();
    }, [groupId, token]);


    const fetchUsers = useCallback(async (search) => {
        if (!search || !token) {
            setOptions([]);
            return;
        }

        setLoading(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/users-permissions/user-emails`,
                {
                    params: { search },
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            const users = Array.isArray(res.data)
                ? res.data.map(user => ({
                    id: user.id,
                    username: user.username,
                    email: user.email
                }))
                : [];

            setOptions(users);
        } catch (err) {
            console.error("Błąd wyszukiwania użytkowników", err);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    }, [token]);

    const debouncedFetch = useMemo(
        () => debounce(fetchUsers, 400),
        [fetchUsers]
    );

    useEffect(() => {
        return () => {
            debouncedFetch.cancel();
        };
    }, [debouncedFetch]);

    useEffect(() => {
        if (inputValue) {
            debouncedFetch(inputValue);
        } else {
            setOptions([]);
        }
    }, [inputValue, debouncedFetch]);

    const handleAddUser = async () => {
        if (!selectedUser || !groupId) return;

        setAdding(true);
        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/users-permissions/groups/${groupId}/add-user`,
                { userId: selectedUser.id },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setGroupUsers(prev => [...prev, selectedUser]);
            onUserAdded?.(selectedUser);
            setSelectedUser(null);
            setInputValue("");
        } catch (err) {
            console.error("Nie udało się dodać użytkownika", err);
        } finally {
            setAdding(false);
        }
    };

    const handleRemoveUser = async (userId) => {
        if (!groupId) return;

        setAdding(true);
        try {
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/users-permissions/groups/${groupId}/remove-user/${userId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setGroupUsers(prev => prev.filter(u => u.id !== userId));
        } catch (err) {
            console.error("Nie udało się usunąć użytkownika", err);
        } finally {
            setAdding(false);
        }
    };

    const getOptionLabel = useCallback((option) => {
        if (!option) return "";
        return option.username
            ? `${option.username} (${option.email})`
            : option.email || "";
    }, []);

    const isOptionEqualToValue = useCallback((option, value) => {
        return option?.id === value?.id;
    }, []);

    return (
        <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
                <Autocomplete
                    sx={{ flex: 1 }}
                    options={options}
                    value={selectedUser ?? null}
                    inputValue={inputValue}
                    isOptionEqualToValue={isOptionEqualToValue}
                    getOptionLabel={getOptionLabel}
                    loading={loading}
                    onInputChange={(event, value, reason) => {
                        setInputValue(value);
                    }}
                    onChange={(event, value) => {
                        setSelectedUser(value ?? null);
                    }}
                    noOptionsText={inputValue ? "Brak wyników" : "Wpisz, aby wyszukać"}
                    filterOptions={(x) => x}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Wyszukaj użytkownika"
                            variant="outlined"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
                <Button
                    variant="contained"
                    disabled={!selectedUser || adding}
                    onClick={handleAddUser}
                >
                    {adding ? "Dodawanie..." : "Dodaj"}
                </Button>
            </Box>

            {groupUsers.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    {groupUsers.map((user) => (
                        <Box
                            key={user.id}
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                p: 2,
                                border: "1px solid #eee",
                                borderRadius: 1,
                                mb: 1,
                                bgcolor: "background.paper",
                            }}
                        >
                            <Box>
                                <strong>{user.username || user.email}</strong>
                                {user.username && (
                                    <Box sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                                        {user.email}
                                    </Box>
                                )}
                            </Box>
                            {user.userRole !== "admin" ? <Button
                                color="error"
                                size="small"
                                onClick={() => handleRemoveUser(user.id)}
                                disabled={adding}
                            >
                                {adding ? "Usuwanie..." : "Usuń"}
                            </Button> : <Chip label={"ADMINISTRATOR"} color="primary" />}

                        </Box>
                    ))}
                </Box>
            )}
        </>
    );
};

export default UserSearchInput;



// import React, { useState, useMemo, useEffect } from "react";
// import axios from "axios";
// import debounce from "lodash.debounce";
// import { TextField, Autocomplete, CircularProgress, Button, Box } from "@mui/material";

// const UserSearchInput = ({ token, groupId, onUserAdded }) => {
//     const [options, setOptions] = useState([]);
//     const [inputValue, setInputValue] = useState("");
//     const [selectedUser, setSelectedUser] = useState(null);
//     const [loading, setLoading] = useState(false);
//     const [adding, setAdding] = useState(false);
//     const [groupUsers, setGroupUsers] = useState([]);
//     const [removingId, setRemovingId] = useState(null);

//     useEffect(() => {
//         const fetchGroupUsers = async () => {
//             try {
//                 const res = await axios.get(
//                     `${process.env.REACT_APP_API_URL}/groups/${groupId}?populate=*`,
//                     {
//                         headers: { Authorization: `Bearer ${token}` },
//                     }
//                 );

//                 const groupData = res.data?.data;

//                 const usersArray = Array.isArray(groupData)
//                     ? groupData.flatMap(d => d.attributes?.users || [])
//                     : groupData?.attributes?.users || [];

//                 setGroupUsers(usersArray);
//             } catch (err) {
//                 console.error("Błąd pobierania użytkowników grupy", err);
//                 setGroupUsers([]);
//             }
//         };

//         if (groupId) fetchGroupUsers();
//     }, [groupId, token]);

//     const fetchUsers = async (search) => {
//         if (!search) {
//             setOptions([]);
//             return;
//         }
//         setLoading(true);
//         try {
//             const res = await axios.get(
//                 `${process.env.REACT_APP_API_URL}/users-permissions/user-emails`,
//                 {
//                     params: { search },
//                     headers: { Authorization: `Bearer ${token}` },
//                 }
//             );
//             setOptions(res.data || []);
//         } catch (err) {
//             console.error(err);
//             setOptions([]);
//         } finally {
//             setLoading(false);
//         }
//     };

//     const debouncedFetch = useMemo(() => debounce(fetchUsers, 400), []);
//     useEffect(() => {
//         return () => debouncedFetch.cancel();
//     }, [debouncedFetch]);

//     const handleAddUser = async () => {
//         if (!selectedUser) return;
//         setAdding(true);
//         try {
//             await axios.post(
//                 `${process.env.REACT_APP_API_URL}/users-permissions/groups/${groupId}/add-user`,
//                 { userId: selectedUser.id },
//                 { headers: { Authorization: `Bearer ${token}` } }
//             );

//             setGroupUsers(prev => [...prev, selectedUser]);

//             onUserAdded && onUserAdded(selectedUser);
//             setSelectedUser(null);
//             setInputValue("");
//             setOptions([]);
//         } catch (err) {
//             console.error("Nie udało się dodać użytkownika", err);
//         } finally {
//             setAdding(false);
//         }
//     };

//     const handleRemoveUser = async (userId) => {
//         setRemovingId(userId);

//         try {
//             await axios.delete(
//                 `${process.env.REACT_APP_API_URL}/users-permissions/groups/${groupId}/remove-user/${userId}`,
//                 { headers: { Authorization: `Bearer ${token}` } }
//             );

//             setGroupUsers(prev => prev.filter(u => u.id !== userId));
//         } catch (err) {
//             console.error("Nie udało się usunąć użytkownika", err);
//         } finally {
//             setRemovingId(null);
//         }
//     };

//     return (
//         <>
//             <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
//                 <Autocomplete
//                     sx={{ flex: 1 }}
//                     options={options || []}
//                     value={selectedUser ?? null}
//                     inputValue={inputValue}
//                     isOptionEqualToValue={(option, value) => option.id === value.id}
//                     onInputChange={(event, value, reason) => {
//                         setInputValue(value);
//                         if (reason === "input") debouncedFetch(value);
//                     }}
//                     getOptionLabel={(option) =>
//                         option?.username
//                             ? `${option.username} (${option.email})`
//                             : option?.email || ""
//                     }
//                     loading={loading}
//                     onChange={(event, value) => {
//                         setSelectedUser(value ?? null);
//                     }}
//                     noOptionsText={inputValue ? "Brak wyników" : "Wpisz, aby wyszukać"}
//                     renderInput={(params) => (
//                         <TextField
//                             {...params}
//                             label="Wyszukaj użytkownika"
//                             variant="outlined"
//                             InputProps={{
//                                 ...params.InputProps,
//                                 endAdornment: (
//                                     <>
//                                         {loading ? <CircularProgress size={20} /> : null}
//                                         {params.InputProps.endAdornment}
//                                     </>
//                                 ),
//                             }}
//                         />
//                     )}
//                 />
//                 <Button
//                     variant="contained"
//                     disabled={!selectedUser || adding}
//                     onClick={handleAddUser}
//                 >
//                     {adding ? "Dodawanie..." : "Dodaj"}
//                 </Button>
//             </Box>

//             <Box sx={{ mt: 3 }}>
//                 {groupUsers.map((user) => (
//                     <Box
//                         key={user.id}
//                         sx={{
//                             display: "flex",
//                             justifyContent: "space-between",
//                             alignItems: "center",
//                             p: 1,
//                             borderBottom: "1px solid #eee",
//                         }}
//                     >
//                         <Box>
//                             {user.attributes?.username
//                                 ? `${user.attributes.username} (${user.attributes.email})`
//                                 : user.attributes?.email}
//                         </Box>

//                         <Button
//                             color="error"
//                             size="small"
//                             onClick={() => handleRemoveUser(user.id)}
//                             disabled={removingId === user.id}
//                         >
//                             {removingId === user.id ? "Usuwanie..." : "Usuń"}
//                         </Button>
//                     </Box>
//                 ))}
//             </Box>
//         </>
//     );
// };

// export default UserSearchInput;


// import React, { useState, useMemo, useEffect } from "react";
// import axios from "axios";
// import debounce from "lodash.debounce";
// import { TextField, Autocomplete, CircularProgress, Button, Box } from "@mui/material";

// const UserSearchInput = ({ token, groupId, onUserAdded }) => {
//     const [options, setOptions] = useState([]);
//     const [inputValue, setInputValue] = useState("");
//     const [selectedUser, setSelectedUser] = useState(null);
//     const [loading, setLoading] = useState(false);
//     const [adding, setAdding] = useState(false);
//     const [groupUsers, setGroupUsers] = useState([]);
//     const [removingId, setRemovingId] = useState(null);

//     useEffect(() => {
//         const fetchGroupUsers = async () => {
//             try {
//                 const res = await axios.get(
//                     `${process.env.REACT_APP_API_URL}/groups/${groupId}?populate=*`,
//                     {
//                         headers: { Authorization: `Bearer ${token}` },
//                     }
//                 );

//                 const groupData = res.data?.data;

//                 const usersArray = Array.isArray(groupData)
//                     ? groupData.flatMap(d => d.attributes?.users || [])
//                     : groupData?.attributes?.users || [];

//                 setGroupUsers(usersArray);

//                 // setGroupUsers(res.data?.data?.attributes?.users || []);

//             } catch (err) {
//                 console.error("Błąd pobierania użytkowników grupy", err);
//             }
//         };

//         if (groupId) fetchGroupUsers();
//     }, [groupId, token]);

//     console.log('groupUsers after init', groupUsers.data)


//     const fetchUsers = async (search) => {
//         if (!search) {
//             setOptions([]);
//             return;
//         }
//         setLoading(true);
//         try {
//             const res = await axios.get(
//                 `${process.env.REACT_APP_API_URL}/users-permissions/user-emails`,
//                 {
//                     params: { search },
//                     headers: { Authorization: `Bearer ${token}` },
//                 }
//             );
//             setOptions(res.data);
//         } catch (err) {
//             console.error(err);
//             setOptions([]);
//         } finally {
//             setLoading(false);
//         }
//     };

//     const debouncedFetch = useMemo(() => debounce(fetchUsers, 400), []);
//     useEffect(() => {
//         return () => debouncedFetch.cancel();
//     }, [debouncedFetch]);

//     const handleAddUser = async () => {
//         if (!selectedUser) return;
//         setAdding(true);
//         try {
//             await axios.post(
//                 `${process.env.REACT_APP_API_URL}/users-permissions/groups/${groupId}/add-user`,
//                 { userId: selectedUser.id },
//                 { headers: { Authorization: `Bearer ${token}` } }
//             );
//             console.log('groupUsersvvvv', groupUsers)
//             setGroupUsers(prev => [...(Array.isArray(prev) ? prev : []), selectedUser]);
//             onUserAdded && onUserAdded(selectedUser);
//             setSelectedUser(null);
//             setInputValue("");
//             setOptions([]);
//         } catch (err) {
//             console.error("Nie udało się dodać użytkownika", err);
//         } finally {
//             setAdding(false);
//         }
//     };

//     const handleRemoveUser = async (userId) => {
//         setRemovingId(userId);

//         try {
//             await axios.delete(
//                 `${process.env.REACT_APP_API_URL}/users-permissions/groups/${groupId}/remove-user/${userId}`,
//                 {
//                     headers: { Authorization: `Bearer ${token}` },
//                 }
//             );

//             setGroupUsers((prev) => prev.filter((u) => u.id !== userId));
//         } catch (err) {
//             console.error("Nie udało się usunąć użytkownika", err);
//         } finally {
//             setRemovingId(null);
//         }
//     };

//     console.log('selectedUser', selectedUser)
//     return (
//         <>
//             <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
//                 <Autocomplete
//                     sx={{ flex: 1 }}
//                     options={options || []}
//                     value={selectedUser ?? null}
//                     inputValue={inputValue}
//                     isOptionEqualToValue={(option, value) => option.id === value.id}
//                     onInputChange={(event, value, reason) => {
//                         setInputValue(value);
//                         if (reason === "input") debouncedFetch(value);
//                     }}
//                     getOptionLabel={(option) =>
//                         option?.username
//                             ? `${option.username} (${option.email})`
//                             : option?.email || ""
//                     }
//                     loading={loading}
//                     onChange={(event, value) => {
//                         setSelectedUser(value ?? null);
//                     }}
//                     noOptionsText={inputValue ? "Brak wyników" : "Wpisz, aby wyszukać"}
//                     renderInput={(params) => (
//                         <TextField
//                             {...params}
//                             label="Wyszukaj użytkownika"
//                             variant="outlined"
//                             InputProps={{
//                                 ...params.InputProps,
//                                 endAdornment: (
//                                     <>
//                                         {loading ? <CircularProgress size={20} /> : null}
//                                         {params.InputProps.endAdornment}
//                                     </>
//                                 ),
//                             }}
//                         />
//                     )}
//                 />
//                 <Button
//                     variant="contained"
//                     disabled={!selectedUser || adding}
//                     onClick={handleAddUser}
//                 >
//                     {adding ? "Dodawanie..." : "Dodaj"}
//                 </Button>
//             </Box>
//             <Box sx={{ mt: 3 }}>
//                 {groupUsers?.data?.map((user) => (
//                     <Box
//                         key={user.id}
//                         sx={{
//                             display: "flex",
//                             justifyContent: "space-between",
//                             alignItems: "center",
//                             p: 1,
//                             borderBottom: "1px solid #eee",
//                         }}
//                     >
//                         <Box>
//                             {user.attributes.username
//                                 ? `${user.attributes.username} (${user.attributes.email})`
//                                 : user.attributes.email}
//                         </Box>

//                         <Button
//                             color="error"
//                             size="small"
//                             onClick={() => handleRemoveUser(user.id)}
//                             disabled={removingId === user.id}
//                         >
//                             {removingId === user.id ? "Usuwanie..." : "Usuń"}
//                         </Button>
//                     </Box>
//                 ))}
//             </Box>
//         </>
//     );
// };

// export default UserSearchInput;


// import React, { useState, useMemo, useEffect } from "react";
// import axios from "axios";
// import debounce from "lodash.debounce";
// import { TextField, Autocomplete, CircularProgress } from "@mui/material";

// const UserSearchInput = ({ token, onSelect }) => {
//     const [options, setOptions] = useState([]);
//     const [inputValue, setInputValue] = useState("");
//     const [loading, setLoading] = useState(false);


//     const fetchUsers = async (search) => {
//         if (!search) {
//             setOptions([]);
//             return;
//         }

//         setLoading(true);
//         try {
//             const res = await axios.get(
//                 `${process.env.REACT_APP_API_URL}/users-permissions/user-emails`,
//                 {
//                     params: { search },
//                     headers: { Authorization: `Bearer ${token}` },
//                 }
//             );
//             setOptions(res.data);
//         } catch (err) {
//             console.error("Błąd pobierania użytkowników", err);
//             setOptions([]);
//         } finally {
//             setLoading(false);
//         }
//     };

//     const debouncedFetch = useMemo(
//         () =>
//             debounce((value) => {
//                 fetchUsers(value);
//             }, 400),
//         []
//     );

//     useEffect(() => {
//         return () => debouncedFetch.cancel();
//     }, [debouncedFetch]);

//     return (
//         <Autocomplete
//             sx={{ mt: 2 }}
//             options={options}
//             inputValue={inputValue}
//             onInputChange={(event, value, reason) => {
//                 setInputValue(value);
//                 if (reason === "input") debouncedFetch(value);
//             }}
//             getOptionLabel={(option) =>
//                 option.username ? `${option.username} (${option.email})` : option.email
//             }
//             loading={loading}
//             onChange={(event, value) => {
//                 if (value) onSelect(value);
//             }}
//             noOptionsText={inputValue ? "Brak wyników" : "Wpisz, aby wyszukać"}
//             renderInput={(params) => (
//                 <TextField
//                     {...params}
//                     label="Wyszukaj użytkownika"
//                     variant="outlined"
//                     InputProps={{
//                         ...params.InputProps,
//                         endAdornment: (
//                             <>
//                                 {loading ? <CircularProgress size={20} /> : null}
//                                 {params.InputProps.endAdornment}
//                             </>
//                         ),
//                     }}
//                 />
//             )}
//         />
//     );
// };

// export default UserSearchInput;